'use client';

import { AisLink as Link, useAisRuDomen } from '@/components/aisearch/ais-link';
import { PUT_API, SSYLKA_NA_API } from '@/lib/ais-api-stranica';
import Image from 'next/image';
import { usePublicPathname } from '@/lib/use-public-pathname';
import { aisTechLocaleSwitch } from '@/lib/locale-switch';
import { useLocale, useTranslations } from 'next-intl';
import { AIS_CONTACT_EMAIL, AIS_RU_CONTACT_EMAIL } from '@/components/seo/schema';
import { AIS_LEGAL_DOCS } from '@/lib/ais-legal';
import { NastrojkiCookie } from '@/components/seo/nastrojki-cookie';
import { SLAGI_ZARUBEZH } from '@/lib/ais-legal-zarubezh-slagi';
import {
  SSYLKI_BIZNESA,
  SSYLKI_PROGRAMM,
  SSYLKI_PROGRAMM_TECH,
  type GruppaSoSsylkami,
} from '@/lib/ais-podval-ssylki';
import { localesWithPosts } from '@/lib/posts';
import { NashiProekty } from '@/components/layout/nashi-proekty';

/**
 * Языки, на которых у блога aisearch есть статьи: ru — это aisearch.ru,
 * en/es/sr — aisearch.tech (06.10.2026).
 */
const JAZYKI_BLOGA: readonly string[] = localesWithPosts('aisearch');
import { PageQr } from '@/components/aisearch/page-qr';

/**
 * Подвал AiSearch.
 *
 * Переключатель языка стоит здесь, а не в шапке: продукт живёт на четырёх
 * языках так же, как основной сайт и DataHunter, но заглавная страница про
 * один сценарий — писать, а не переключать��я, поэтому шапка остаётся
 * короткой, а выбор языка не первое, что видит человек.
 *
 * Правовая колонка обязательна, а не желательна: сервис берёт деньги, и
 * стоимость, соглашение, политика и оферта должны быть доступны с любой
 * страницы. Их отсутствие — это и претензия проверяющего, и отказ
 * платёжного провайдера при подключении эквайринга.
 *
 * Список документов берётся из реестра `ais-legal.ts`, а не переписывается
 * здесь ссылками: появится реферальное соглашение — оно появится и в
 * подвале, без правки вёрстки и без риска, что страница есть, а ссылки на
 * неё нет.
 */
/**
 * `gruppy` считает сервер (`gruppySoSsylkami`): разрешение адресов идёт по
 * реестру страниц инструментов, и импорт его отсюда увозил бы реестр в JS
 * каждой страницы.
 */
export function AiSearchFooter({ gruppy }: { gruppy: GruppaSoSsylkami[] }) {
  const t = useTranslations('ais_layout');
  const pathname = usePublicPathname();
  const year = String(new Date().getFullYear());
  // Домен известен уже на сервере: признак приходит из layout витрины.
  // Определять его на клиенте нельзя — тогда в готовом HTML остаётся
  // адрес чужого домена, и поисковик с читалкой видят именно его.
  const odinJazyk = useAisRuDomen();
  const locale = useLocale();
  // Документы (05.10.2026, владелец): российские — только на aisearch.ru.
  // На aisearch.tech свой набор под европейский рынок на языке страницы,
  // русской версии .tech — английская редакция: русскоязычные бывают не
  // только в России, а российское право к .tech не относится.
  const slagiDokumentov: readonly string[] = odinJazyk
    ? // Черновик открывается только по прямой ссылке, до одобрения
      // владельцем в подвале его нет.
      AIS_LEGAL_DOCS.filter((doc) => doc.chernovik !== true).map((doc) => doc.slug)
    : SLAGI_ZARUBEZH;
  // На русском домене отвечает другой ящик: письмо на .tech ушло бы
  // команде, которая по-русски не отвечает.
  const email = odinJazyk ? AIS_RU_CONTACT_EMAIL : AIS_CONTACT_EMAIL;

  return (
    <footer className="border-t border-[var(--color-border)] bg-[var(--color-bg)]">
      <div className="max-w-[1280px] mx-auto px-6 py-12">
        {/* Сначала ссылки, логотип с подписью — под ними.

            Подвал начинают читать снизу вверх, дойдя до конца страницы, и
            первым делом ищут в нём ссылку, а не название сайта: на каком
            сайте человек находится, он к этому моменту уже знает. */}
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-6">
          {/* Инструменты тремя группами по задаче человека, а не одним
              списком. Четырнадцать ссылок в столбец — простыня, где глазу
              не за что зацепиться, а вес ссылок размазывается ровным
              слоем.

              Названия групп от задачи: на легаси они назывались «GPT» и
              «Ai Media», а человек не знает, что «GPT» про тексты, а
              «Ai Media» про звук и картинки.

              Порядок внутри групп по весу трафика (см. `ais-podval.ts`):
              подвал сквозной, ссылки выше получают больше веса, и
              отдавать его логично тем, кто уже собирает трафик.

              Список из реестра страниц, а не отдельный здесь: иначе
              повторится история с лентой пилюль — страница есть, в
              реестре есть, а в подвале забыли. */}
          {gruppy.map(({ klyuchZagolovka, ssylki }) => {
            // Пустых групп сервер не присылает: на международной витрине
            // этих страниц нет, и заголовок без ссылок под ним выглядел бы
            // недоделкой. Группы инструментов — только русская витрина.
            if (!odinJazyk) return null;

            return (
              <div key={klyuchZagolovka}>
                <p className="font-semibold text-sm mb-4">{t(klyuchZagolovka)}</p>
                <ul className="space-y-2">
                  {ssylki.map((ssylka) => (
                    <li key={ssylka.href}>
                      <Link
                        href={ssylka.href}
                        className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                      >
                        {t(ssylka.klyuchPodpisi)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Колонка «Для бизнеса» (#187). Только на русской витрине:
              раздел есть только там, на .tech ссылки вели бы в 404.
              Стоит перед «Полезными ссылками» (правка владельца 28.09). */}
          {odinJazyk && (
            <div>
              <p className="font-semibold text-sm mb-4">{t('group_business')}</p>
              <ul className="space-y-2">
                {SSYLKI_BIZNESA.map((ssylka) => (
                  <li key={ssylka.href}>
                    <Link
                      href={ssylka.href}
                      className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                    >
                      {t(ssylka.klyuchPodpisi)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Программы — на обеих витринах, адреса у каждой свои. */}
          <div>
            <p className="font-semibold text-sm mb-4">{t('group_programmy')}</p>
            <ul className="space-y-2">
              {(odinJazyk
                ? SSYLKI_PROGRAMM
                : SSYLKI_PROGRAMM_TECH.filter((s) => !s.jazyki || s.jazyki.includes(locale))
              ).map((ssylka) => (
                <li key={ssylka.href}>
                  <Link
                    href={ssylka.href}
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                  >
                    {t(ssylka.klyuchPodpisi)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-semibold text-sm mb-4">{t('useful')}</p>
            <ul className="space-y-2">
              {/* Блог (02.10.2026, на .tech с 06.10.2026): только на языке,
                  где есть хотя бы одна статья — ссылка на пустую витрину
                  вела бы на страницу, закрытую от поиска. На .tech/ru блога
                  нет: русские статьи живут на aisearch.ru. */}
              {(odinJazyk
                ? JAZYKI_BLOGA.includes('ru')
                : locale !== 'ru' && JAZYKI_BLOGA.includes(locale)) && (
                <li>
                  <Link
                    href="/blog"
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                  >
                    {t('footer_blog')}
                  </Link>
                </li>
              )}
              <li>
                <Link
                  href="/pricing"
                  className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                >
                  {t('pricing')}
                </Link>
              </li>
              {/* API с оплатой в рублях — только русская витрина (12.10.2026). */}
              {odinJazyk && (
                <li>
                  <Link
                    href={PUT_API}
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                  >
                    {SSYLKA_NA_API.podval}
                  </Link>
                </li>
              )}
              {slagiDokumentov.map((slug) => (
                <li key={slug}>
                  <Link
                    href={`/legal/${slug}`}
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                  >
                    {t(`legal_${slug.replaceAll('-', '_')}`)}
                  </Link>
                </li>
              ))}
              {/* Плашка согласия есть только на aisearch.tech (16.10.2026). */}
              {!odinJazyk && (
                <NastrojkiCookie
                  kakLi
                  className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                />
              )}
              {/* Контакты переехали сюда из своей колонки (#187, правка
                  владельца 27.09): освободившееся место занял раздел для
                  бизнеса. Почта и страница контактов по-прежнему в
                  подвале каждой страницы. */}
              <li>
                <a
                  href={`mailto:${email}`}
                  className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                >
                  {email}
                </a>
              </li>
              {/* «Вижу» (#190): обычная ссылка, а не Link: страница
                  живёт вне дерева витрины и грузится целиком. */}
              {odinJazyk && (
                <li>
                  <a
                    href="/vizhu"
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                  >
                    Помощник для незрячих
                  </a>
                </li>
              )}
              {/* Реквизиты и Telegram есть только у русской витрины:
                  российский ИНН ничего не значит для покупателя из
                  другой страны, и страницы на .tech просто нет. */}
              {odinJazyk && (
                <li>
                  <Link
                    href="/contacts"
                    className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
                  >
                    {t('nav_contacts')}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* На aisearch.ru выбора языка нет: домен обслуживает одну
              русскую версию (lib/ais-ru-domen.ts). Показывать ссылки на
              версии, которых на этом домене не существует, значило бы
              вести человека в тупик. */}
          {!odinJazyk && (
            <div>
              <p className="font-semibold text-sm mb-4">{t('language')}</p>
              {/* Тот же набор языков, что у DataHunter и основного сайта:
                  платформа собирается из одного образа, и локали общие. */}
              <div className="flex flex-wrap gap-2">
                {(['en', 'es', 'sr', 'ru'] as const).map((locale) => {
                  // Русский уводит на aisearch.ru, страница программы без
                  // перевода — в список программ (lib/locale-switch.ts).
                  const cel = aisTechLocaleSwitch(pathname, locale);
                  return 'adres' in cel ? (
                    <a
                      key={locale}
                      href={cel.adres}
                      hrefLang={locale}
                      className="px-2 py-1 rounded text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] border border-[var(--color-border)] hover:border-[var(--color-accent-500)] transition-colors uppercase"
                    >
                      {locale}
                    </a>
                  ) : (
                    <Link
                      key={locale}
                      href={cel.put}
                      locale={locale}
                      hrefLang={locale}
                      className="px-2 py-1 rounded text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] border border-[var(--color-border)] hover:border-[var(--color-accent-500)] transition-colors uppercase"
                    >
                      {locale}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Код текущей страницы, а не главной: человек читает конкретную
              страницу и хочет продолжить с телефона именно её. */}
          <div className="col-span-2 md:col-span-1 md:justify-self-end">
            <PageQr label={t('qr_hint')} />
          </div>
        </div>

        <div className="mt-12 max-w-md">
          {/* Значок тот же, что в шапке, только крупнее, плюс название
              словом.

              Раньше здесь стоял полный логотип из SVG, и на нём нарисован
              адрес aisearch.tech. На русской витрине это читалось как
              приглашение уйти на чужой домен — четвёртая по счёту
              протечка .tech в этой серии (#189).

              Подменить надпись внутри картинки нельзя: домен там нарисован
              кривыми, текста в файле нет. Поэтому берём значок без домена,
              а название пишем словом — так оно ещё и читается поиском и
              экранными читалками, чего картинка не умеет. */}
          <div className="mb-3 flex items-center gap-3">
            <Image
              src="/icons/aisearch/logo-115.png"
              alt=""
              // Значок рядом с названием словом: повторять его в alt незачем,
              // а aria-hidden говорит анализаторам, что картинка декоративная.
              aria-hidden="true"
              width={56}
              height={56}
              className="h-14 w-14"
            />
            {/* На русской витрине имя кириллицей (правка владельца 28.09):
                так его и ищут («айсерч»), см. llms-ais-ru.ts. На .tech
                и остальных языках прежнее латинское. */}
            <span className="text-2xl font-bold tracking-tight">
              {odinJazyk ? 'АйСёрч' : 'AiSearch'}
            </span>
          </div>
          <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">{t('tagline')}</p>
        </div>

        {/* Соседние продукты (перелинковка, 19.10.2026) — только на .tech:
            aisearch.ru российский контур и в перелинковке не участвует. */}
        {!odinJazyk && <NashiProekty sajt="aisearch" locale={locale} />}

        <div className="mt-12 pt-8 border-t border-[var(--color-border)] space-y-4">
          <p className="text-xs text-[var(--color-text-muted)]">{t('copyright', { year })}</p>

          {/*
            Юридическая приписка.

            Мелким шрифтом и приглушённым цветом намеренно: это не текст
            для чтения, а обязательная сноска. Но она обязана быть, и
            обязана быть на странице, а не в документе по ссылке.

            Два содержательных пункта. Первый: чужие названия и логотипы
            принадлежат своим владельцам, и упоминание сервиса не означает
            ни партнёрства, ни одобрения с их стороны. Второй: организации
            Meta (Facebook, Instagram) признаны в России экстремистскими и
            запрещены, и при упоминании их названий эта пометка требуется
            законом.

            Цвет приглушённый, но без прозрачности: с `/70` сноска давала
            3,66:1 на тёмном фоне при норме 4,5:1 (#213). Второстепенность
            и так видна по размеру шрифта.
          */}
          <p className="text-[11px] leading-relaxed text-[var(--color-text-muted)]">
            {t('legal_note')}
          </p>
        </div>
      </div>
    </footer>
  );
}
